import {FormProvider, useForm} from "react-hook-form";
import * as yup from "yup";
import {useTranslation} from "react-i18next";
import {Button, Grid} from "@mui/material";
import React, {useContext, useEffect, useState} from "react";
import {useRecoilState} from "recoil";
import {commercialCategoryListAtom} from "../../../state/commercialCategoriesRecoil";
import {commercialSegmentListAtom} from "../../../state/commercialSegmentsAtom";
import SectionHeader from "../../../../../util/component/SectionHeader";
import {Box} from "@mui/system";
import {
    ExceptionListProductCodeLocationGroupDTO,
    ExceptionListProductCodeLocationGroupEntityResponse,
    FormProductCodeLocationGroupExceptionListAssociation,
    LocationGroupAssociation,
    ProductCodeLocationGroupAssociation,
} from "../../../api/types/exceptionListProductCodeLocationGroup";
import {CommercialCategoryEntityResponse, CommercialCategorySelectFE} from "../../../api/types/commercialCategory";
import {fromGenericBeToGenericFe, fromLocationGroupProductCodeBeToFe} from "../../../utils";
import {FormDialog, SelectElementModelFE, UseLocationStateModel} from "../../../api/types/generic";
import {useCommercialSegmentRepository} from "../../../api/repositories/CommercialSegmentRepository";
import {useCommercialCategoryRepository} from "../../../api/repositories/CommercialCategoryRepository";
import {productCodesAtom} from "../../../state/locationGroupProductCodesRecoil";
import CardContainer from "../../../common/components/CardContainer";
import {useProductCodeRepository} from "../../../api/repositories/ProductCodeRepository";
import {FormAutocomplete} from "../../../common/form-fields";
import {useProductCodeLocationGroupRepository} from "../../../api/repositories/ProductCodeLocationGroupRepository";
import {useLocation, useNavigate} from "react-router-dom";
import {exceptionListSelectListAtom} from "../../../state/exceptionListsRecoil";
import {useExceptionListRepository} from "../../../api/repositories/ExceptionListRepository";
import {ExceptionListEntityResponse, ExceptionListSelectFE} from "../../../api/types/exceptionList";
import {
    useExceptionListProductCodeLocationGroupRepository
} from "../../../api/repositories/ExceptionListProductCodeLocationGroupRepository";
import {ProductCodeSelectFE} from "../../../api/types/productCode";
import Loading from "../../../common/components/Loading";
import {LocationGroupProductCodeModelFE} from "../../../api/types/locationGroupProductCode";
import FieldArray from "./fieldArray";
import {yupResolver} from "@hookform/resolvers/yup";
import DealerAndTenant from "../../../common/components/DealerAndTenant";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";

const ExceptionListProductCodeLocationGroupForm = ({
                                                       readOnly,
                                                   }: {
    readOnly: boolean;
}) => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Navigate
    const navigate = useNavigate();

    const {globalData: {user}} = useContext(GlobalContext);

    // Use Location
    const location = useLocation();

    const locationState: UseLocationStateModel =
        location.state as UseLocationStateModel;

    //PER CHI ARRIVA DA SALES&MARKETING, NAVIGARE SU QUESTA PAGINA INSERENDO NELLO STATE DELLA PAGINA IL GRUPPO di FRANCHGIE
    //navigate("/pricing/product-code/location-group-exception-list/create", {replace: true, state: {exceptionListId: "1"}});

    // Use State
    const [formDialog] = useState<FormDialog | null>(null);
    const [initialValues] =
        useState<FormProductCodeLocationGroupExceptionListAssociation>({
            commercialCategoryId: null,
            commercialSegmentId: null,
            exceptionList: null,
            associations: [],
            dealer: null,
            legalEntity: null,
        });
    const [oldAssociations, setOldAssociations] = useState<ExceptionListProductCodeLocationGroupDTO[]>([]);
    const [locationGroupList, setLocationGroupList] = useState<{ [key: number]: LocationGroupProductCodeModelFE[] }>({});

    // Use Recoil
    const [commercialCategoryList, setCommercialCategoryList] = useRecoilState(commercialCategoryListAtom);
    const [commercialSegmentList, setCommercialSegmentList] = useRecoilState(commercialSegmentListAtom);
    const [productCodeList, setProductCodeList] = useRecoilState(productCodesAtom);
    const [exceptionLists, setExceptionLists] = useRecoilState(exceptionListSelectListAtom);

    // Use Repository Hooks
    const {getCommercialSegments} = useCommercialSegmentRepository();
    const {
        getCommercialCategories,
        getCommercialCategoriesByCommercialSegmentId
    } = useCommercialCategoryRepository();
    const {getExceptionList, getExceptionListsByCommercialCategoryId} = useExceptionListRepository();
    const {getLocationGroupsByProductCode} = useProductCodeLocationGroupRepository();
    const {getProductCodesByCommercialCategoryId} = useProductCodeRepository();
    const {
        getAssociations,
        postExceptionListProductCodeLocationGroupAll
    } = useExceptionListProductCodeLocationGroupRepository();

    const handleGetStaticContent = async () => {
        // TODO: togliere COMMERCIAL_SEGMENTS || quando si pusha
        // const commercialSegmentsData = COMMERCIAL_SEGMENTS || await getCommercialSegments();
        const commercialSegmentsData = await getCommercialSegments();

        const commercialSegmentList: SelectElementModelFE[] =
            fromGenericBeToGenericFe(commercialSegmentsData);
        setCommercialSegmentList(commercialSegmentList);
    };

    // ************ FORM - START ************ //
    const validationSchema = yup
        .object()
        .shape({
            dealer: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string(),
                })
                .required(t("translation:validation.fieldMandatory"))
                .nullable(true),
            legalEntity: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string(),
                })
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
            commercialSegmentId: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string(),
                })
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
            commercialCategoryId: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string(),
                })
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
            exceptionList: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string(),
                })
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
            associations: yup
                .array()
                .of(
                    yup.object().shape({
                        locationGroup: yup
                            .array()
                            .of(
                                yup.object().shape({
                                    code: yup.string(),
                                    label: yup.string(),
                                })
                            )
                            .min(1, t("pricing:validation.minOne"))
                            .nullable()
                            .required(t("translation:validation.fieldMandatory")),
                        productCode: yup
                            .array()
                            .of(
                                yup.object().shape({
                                    code: yup.string(),
                                    label: yup.string(),
                                })
                            )
                            .min(1, t("pricing:validation.minOne"))
                            .nullable()
                            .required(t("translation:validation.fieldMandatory")),
                    })
                )
                .nullable()
                .min(0),
        })
        .defined();

    // Use Form
    const form = useForm<FormProductCodeLocationGroupExceptionListAssociation>({
        defaultValues: initialValues,
        resolver: yupResolver(
            validationSchema
        ),
        shouldUnregister: true
    });
    const {
        control,
        register,
        getValues,
        formState: {errors},
        setValue
    } = form;
    const watchAllForm = form.watch();
    // ************ FORM - END ************ //

    // Use Effect
    useEffect(() => {
        handleGetStaticContent();
    }, []);

    // Ri-filtra le categorie quando cambia la legal entity
    useEffect(() => {
        const refetchCategories = async () => {
            const segmentCode = watchAllForm?.commercialSegmentId
                ? (watchAllForm.commercialSegmentId as SelectElementModelFE).code as string
                : null;
            if (!segmentCode) return;

            const commercialCategoriesData = await getCommercialCategoriesByCommercialSegmentId(segmentCode);
            const allCategories: CommercialCategorySelectFE[] = fromGenericBeToGenericFe(commercialCategoriesData);
            
            form.setValue("commercialCategoryId", null);
            form.setValue("exceptionList", null);
            setCommercialCategoryList(filterCategoriesByLegalEntity(allCategories));
        };
        refetchCategories();
    }, [watchAllForm?.legalEntity]);

    useEffect(() => {
        //atterraggio con gruppo di eccezioni già selezionato
        const handleGetContent = async () => {
            if (form && locationState?.exceptionListId) {
                //ottenere gruppo di eccezioni da id
                const exceptionListResponse = await getExceptionList(locationState?.exceptionListId)
                setValue("legalEntity", {
                    code: exceptionListResponse.legalEntity,
                    label: t('translation:legalEntities.'.concat(exceptionListResponse.legalEntity))
                })
                setValue("dealer", {
                    code: exceptionListResponse.dealer,
                    label: t('translation:dealers.'.concat(exceptionListResponse.dealer))
                })
                const exceptionListEntityResponse: ExceptionListEntityResponse =
                    await getExceptionList(Number(locationState?.exceptionListId));
                //ottenere lista segmenti commerciali
                // TODO: togliere COMMERCIAL_SEGMENTS || quando si pusha
                // const commercialSegmentsData = COMMERCIAL_SEGMENTS || await getCommercialSegments();
                const commercialSegmentsData = await getCommercialSegments();

                const commercialSegmentList: SelectElementModelFE[] =
                    fromGenericBeToGenericFe(commercialSegmentsData);
                setCommercialSegmentList(commercialSegmentList);

                //ottenere categoria commerciale specifica del gruppo di eccezioni
                const commercialCategoryEntityResponse: CommercialCategoryEntityResponse =
                    exceptionListEntityResponse.commercialCategory;

                //selezionare segmento commerciale corrispondente a categoria commerciale in gruppo di eccezioni
                const commercialSegment: SelectElementModelFE | undefined =
                    commercialSegmentList.find(
                        (cs) =>
                            cs.code === commercialCategoryEntityResponse.commercialSegment.id
                    );
                commercialSegment && setValue("commercialSegmentId", commercialSegment);

                //ottenere lista di categorie commerciali del segmento commerciale
                const commercialCategoriesData =
                    await getCommercialCategoriesByCommercialSegmentId(
                        commercialSegment?.code as string
                    );
                const commercialCategoryList: CommercialCategorySelectFE[] =
                    fromGenericBeToGenericFe(commercialCategoriesData);
                
                // Filtra per legal entity
                const legalEntityCode = exceptionListResponse.legalEntity as string;
                const filteredCategories = new Map<string, CommercialCategorySelectFE>();
                commercialCategoryList.forEach(category => {
                    category.labels?.forEach(labelObj => {
                        if (labelObj.country === legalEntityCode) {
                            const key = labelObj.label;
                            if (!filteredCategories.has(key)) {
                                filteredCategories.set(key, {
                                    ...category,
                                    label: labelObj.label
                                });
                            }
                        }
                    });
                });
                setCommercialCategoryList(Array.from(filteredCategories.values()));

                //selezionare categoria commerciale di gruppo di eccezioni
                const commercialCategory: CommercialCategorySelectFE | undefined =
                    commercialCategoryList.find(
                        (cc) => cc.code === commercialCategoryEntityResponse.id
                    );
                commercialCategory &&
                setValue("commercialCategoryId", commercialCategory);

                //ottenere lista di gruppi di eccezioni da categoria commerciale
                const exceptionListsData =
                    await getExceptionListsByCommercialCategoryId(
                        commercialCategory?.code as string, user.dealers.length === 1 ? user.dealers[0].name as string : watchAllForm.dealer?.code as string || exceptionListResponse.dealer, user.legalEntities.length === 1 ? user.legalEntities[0].name as string : watchAllForm.legalEntity?.code as string || exceptionListResponse.legalEntity
                    );
                const exceptionLists: ExceptionListSelectFE[] =
                    fromGenericBeToGenericFe(exceptionListsData);
                setExceptionLists(exceptionLists);

                const productCodeData = await getProductCodesByCommercialCategoryId(commercialCategory?.code as string, user.dealers.length === 1 ? user.dealers[0].name as string : watchAllForm.dealer?.code as string || exceptionListResponse.dealer, user.legalEntities.length === 1 ? user.legalEntities[0].name as string : watchAllForm.legalEntity?.code as string || exceptionListResponse.legalEntity);
                setProductCodeList([{
                    code: 0,
                    label: t("pricing:actions.selectAll")
                }, ...fromGenericBeToGenericFe(productCodeData).map(el => ({
                    code: el.code,
                    label: el.code + " - " + el.label
                }))]);

                //selezionare gruppo di eccezioni
                const exceptionList: ExceptionListSelectFE | undefined = exceptionLists.find(pc => pc.code === exceptionListEntityResponse.id);
                exceptionList && setValue("exceptionList", exceptionList);
            }
        };
        handleGetContent();
    }, [locationState?.exceptionListId]);

    useEffect(() => {
        const handleGetContent = async () => {
            const exceptionListId = watchAllForm?.exceptionList?.code as number;
            if (exceptionListId) {
                //get di associazioni già esistenti
                const associationsBe = await getAssociations(exceptionListId);

                const associations: ExceptionListProductCodeLocationGroupEntityResponse[] =
                    associationsBe.filter(
                        (el: ExceptionListProductCodeLocationGroupEntityResponse) =>
                            el.productCode.length > 0
                    );

                //get di locationGroups per ciascun prodotto nelle associazioni già esistenti
                let currentLocationGroupList: {
                    [key: number]: LocationGroupProductCodeModelFE[];
                } = {};
                for (const association of associations) {
                    const index: number = associations.indexOf(association);
                    let locationGroups: LocationGroupProductCodeModelFE[] = [];
                    for (let i = 0; i < association.productCode.length; i++) {
                        const locationGroupsData = await getLocationGroupsByProductCode(
                            association.productCode[i]
                        );

                        const currentLocationGroupList: LocationGroupProductCodeModelFE[] =
                            fromLocationGroupProductCodeBeToFe(
                                locationGroupsData,
                                association.productCode[i]
                            );
                        locationGroups = locationGroups.concat(currentLocationGroupList);
                    }
                    currentLocationGroupList = {
                        ...currentLocationGroupList,
                        [index]: locationGroups,
                    };
                }

                setLocationGroupList(currentLocationGroupList);

                const productCodeLocationGroupExceptionListAssociation: ProductCodeLocationGroupAssociation[] =
                    associations.length > 0
                        ? associations.map((association) => {
                            const productCodes: ProductCodeSelectFE[] =
                                productCodeList.filter((pc) => {
                                    return association.productCode?.find((productCode) => {
                                        return productCode === pc.code;
                                    });
                                });

                            return {
                                locationGroup: association.associations?.map((lg: LocationGroupAssociation) => ({
                                    code: lg.id,
                                    label: lg.name,
                                    productCodeId: lg.prcProductCode
                                })),
                                productCode: productCodes || []
                            };
                        })
                        :
                        [{
                            locationGroup: [],
                            productCode: []
                        }];
                setValue("associations", productCodeLocationGroupExceptionListAssociation);

                //creazione lista di associazioni già esistenti
                const oldAssociations: ExceptionListProductCodeLocationGroupDTO[] = [];

                associations.forEach(
                    (
                        association: ExceptionListProductCodeLocationGroupEntityResponse
                    ) => {
                        association.associations.forEach(
                            (locationGroup: LocationGroupAssociation) => {
                                association.productCode.forEach((productCode: string) => {
                                    if (productCode === locationGroup.prcProductCode) {
                                        oldAssociations.push({
                                            exceptionListId,
                                            locationGroupId: locationGroup.id,
                                            productCodeId: productCode,
                                        });
                                    }
                                });
                            }
                        );
                    }
                );

                setOldAssociations(oldAssociations);

            }
        };
        handleGetContent();
    }, [watchAllForm?.exceptionList]);

    const filterCategoriesByLegalEntity = (allCategories: CommercialCategorySelectFE[]): CommercialCategorySelectFE[] => {
        const selectedLegalEntity = watchAllForm?.legalEntity;
        const legalEntityCode = selectedLegalEntity ? selectedLegalEntity.code as string : null;
        const expandedCategories = new Map<string, CommercialCategorySelectFE>();
        allCategories.forEach(category => {
            if (category.labels && category.labels.length > 0) {
                category.labels.forEach(labelObj => {
                    if (!legalEntityCode || labelObj.country === legalEntityCode) {
                        const key = labelObj.label;
                        if (!expandedCategories.has(key)) {
                            expandedCategories.set(key, {
                                ...category,
                                label: labelObj.label
                            });
                        }
                    }
                });
            } else {
                expandedCategories.set(category.label, category);
            }
        });
        return Array.from(expandedCategories.values());
    };

    const handleChangeCommercialSegment = async (value: SelectElementModelFE) => {
        if (value) {
            form.setValue("commercialSegmentId", value);
            const commercialCategoriesData =
                await getCommercialCategoriesByCommercialSegmentId(
                    (value as SelectElementModelFE).code as string
                );
            const commercialCategoryList: CommercialCategorySelectFE[] =
                fromGenericBeToGenericFe(commercialCategoriesData);
            form.setValue("commercialCategoryId", null);
            form.setValue("exceptionList", null);
            setCommercialCategoryList(filterCategoriesByLegalEntity(commercialCategoryList));
        } else {
            form.setValue("commercialSegmentId", value);
            const commercialCategoriesData: CommercialCategoryEntityResponse[] =
                await getCommercialCategories();

            const commercialCategoryList: CommercialCategorySelectFE[] =
                fromGenericBeToGenericFe(commercialCategoriesData);
            form.setValue("commercialCategoryId", null);
            form.setValue("exceptionList", null);
            form.setValue("associations", []);

            setCommercialCategoryList(filterCategoriesByLegalEntity(commercialCategoryList));
        }
    };

    const handleChangeCommercialCategory = async (
        value: CommercialCategorySelectFE
    ) => {
        if (value) {
            form.setValue("commercialCategoryId", value);
            const exceptionListsData = await getExceptionListsByCommercialCategoryId(
                (value as CommercialCategorySelectFE).code as string, user.dealers.length === 1 ? user.dealers[0].name as string : watchAllForm.dealer?.code as string, user.legalEntities.length === 1 ? user.legalEntities[0].name as string : watchAllForm.legalEntity?.code as string
            );
            const exceptionLists: ExceptionListSelectFE[] =
                fromGenericBeToGenericFe(exceptionListsData);
            form.setValue("exceptionList", null);
            setExceptionLists(exceptionLists);

            const productCodesData = await getProductCodesByCommercialCategoryId((value as CommercialCategorySelectFE).code as string, user.dealers.length === 1 ? user.dealers[0].name as string : watchAllForm.dealer?.code as string, user.legalEntities.length === 1 ? user.legalEntities[0].name as string : watchAllForm.legalEntity?.code as string);
            setProductCodeList([{
                code: 0,
                label: t("pricing:actions.selectAll")
            }, ...fromGenericBeToGenericFe(productCodesData).map(el => ({
                code: el.code,
                label: el.code + " - " + el.label
            }))]);
        } else {
            form.setValue("commercialCategoryId", value);
            form.setValue("exceptionList", null);
            form.setValue("associations", []);

            setProductCodeList([]);
            setExceptionLists([]);
        }
    };

    const handleChangeExceptionList = async (value: SelectElementModelFE) => {
        form.setValue("exceptionList", value);
        if (!value) {
            form.setValue("associations", []);
            setOldAssociations([]);
        }
    };

    const handleChangeProductCode = async (
        value: ProductCodeSelectFE[],
        index: number
    ) => {
        let currentValue: ProductCodeSelectFE[];
        if (
            // è stato cliccato "selectAll
            value.find(
                (el: ProductCodeSelectFE) => el.label === t("pricing:actions.selectAll")
            ) &&
            // tra i vecchi valori non c'è "selectAll
            !getValues().associations[index].productCode?.find(
                (el: ProductCodeSelectFE) => el.label === t("pricing:actions.selectAll")
            )
        ) {
            // applica il "selectAll, verranno selezionati anche tutti i gruppi di location e sarà disabilitato l'input dei gruppi di location
            currentValue = productCodeList.filter(
                (el: ProductCodeSelectFE) => el.label !== t("pricing:actions.selectAll")
            );
        } else {
            // setta solo i prodotti selezionati
            currentValue = value.filter(
                (el: ProductCodeSelectFE) => el.label !== t("pricing:actions.selectAll")
            );
        }

        if (currentValue.length > 1) {
            let locationGroups: LocationGroupProductCodeModelFE[] = [];
            // get di tutti i location group associati ai prodotti selezionati
            for (let i = 0; i < currentValue.length; i++) {
                const locationGroupsData = await getLocationGroupsByProductCode(currentValue[i].code as string);
                const currentLocationGroupList: LocationGroupProductCodeModelFE[] = fromLocationGroupProductCodeBeToFe(locationGroupsData, currentValue[i].code as string);
                locationGroups = locationGroups.concat(currentLocationGroupList);
            }
            setLocationGroupList({...locationGroupList, [index]: locationGroups});
            const currentAssociations = [...getValues().associations];
            currentAssociations.splice(index, 1, {
                ...currentAssociations[index],
                productCode: currentValue,
                locationGroup: locationGroups
            });
            setValue("associations", currentAssociations);
        } else if (currentValue.length === 1) {
            // get di tutti i location group associati al prodotto selezionato

            let locationGroups: LocationGroupProductCodeModelFE[] = [];
            // get di tutti i location group associati ai prodotti selezionati
            for (let i = 0; i < currentValue.length; i++) {
                const locationGroupsData = await getLocationGroupsByProductCode(currentValue[i].code as string);
                const currentLocationGroupList: LocationGroupProductCodeModelFE[] = fromLocationGroupProductCodeBeToFe(locationGroupsData, currentValue[i].code as string);
                locationGroups = locationGroups.concat(currentLocationGroupList);
            }
            setLocationGroupList({
                ...locationGroupList, [index]: [{
                    code: 0,
                    label: t("pricing:actions.selectAll"),
                    productCodeId: ""
                }, ...locationGroups]
            });

            const currentAssociations = [...getValues().associations];
            currentAssociations.splice(index, 1, {
                ...currentAssociations[index],
                productCode: currentValue,
                locationGroup: locationGroups
            });
            setValue("associations", currentAssociations);
        } else {
            const currentAssociations = [...getValues().associations];
            currentAssociations.splice(index, 1, {
                ...currentAssociations[index],
                productCode: currentValue,
                locationGroup: []
            });
            setValue("associations", currentAssociations);
        }
    };

    const handleChangeLocationGroup = async (
        value: LocationGroupProductCodeModelFE[],
        index: number
    ) => {
        const currentValue: LocationGroupProductCodeModelFE[] =
            // è stato cliccato "selectAll
            value.find(
                (el: LocationGroupProductCodeModelFE) =>
                    el.label === t("pricing:actions.selectAll")
            ) &&
            // tra i vecchi valori non c'è "selectAll
            !getValues().associations[index].locationGroup?.find(
                (el: LocationGroupProductCodeModelFE) =>
                    el.label === t("pricing:actions.selectAll")
            )
                ? // applica il "selectAll
                locationGroupList[index].filter(
                    (el: LocationGroupProductCodeModelFE) =>
                        el.label !== t("pricing:actions.selectAll")
                )
                : // setta solo i car group selezionati
                value.filter(
                    (el: LocationGroupProductCodeModelFE) =>
                        el.label !== t("pricing:actions.selectAll")
                );

        const currentAssociations = [...getValues().associations];
        currentAssociations.splice(index, 1, {
            ...currentAssociations[index],
            locationGroup: currentValue
        });
        setValue("associations", currentAssociations);
    };

    const handleConfirm = async () => {
        const exceptionListId = getValues()?.exceptionList?.code as number;
        const currentAssociationsArray: ExceptionListProductCodeLocationGroupDTO[] =
            [];

        //costruire array associazioni
        getValues().associations.forEach((association) => {
            association.locationGroup.forEach(
                (lg: LocationGroupProductCodeModelFE) => {
                    association.productCode.forEach(
                        (productCode: SelectElementModelFE) => {
                            if (productCode.code === lg.productCodeId) {
                                const currentAssociation: ExceptionListProductCodeLocationGroupDTO =
                                    {
                                        exceptionListId,
                                        locationGroupId: Number(lg.code),
                                        productCodeId: String(productCode.code),
                                    };
                                currentAssociationsArray.push(currentAssociation);
                            }
                        }
                    );
                }
            );
        });

        // let difference = arr1.filter(x => !arr2.includes(x));
        // includes === arr2.filter(e => e.locationGroupId === x.locationGroupId).length > 0

        //current - old => associazioni da inserire
        const associazioniDaInserire: ExceptionListProductCodeLocationGroupDTO[] =
            currentAssociationsArray.filter((ca) => {
                return !(
                    oldAssociations.filter((oa) => {
                        return (
                            oa.exceptionListId === ca.exceptionListId &&
                            oa.locationGroupId === ca.locationGroupId &&
                            ca.productCodeId === oa.productCodeId
                        );
                    })?.length > 0
                );
            });
        //old - current => associazioni da togliere
        const associazioniDaEliminare: ExceptionListProductCodeLocationGroupDTO[] =
            oldAssociations.filter((oa) => {
                return !(
                    currentAssociationsArray.filter((ca) => {
                        return (
                            ca.exceptionListId === oa.exceptionListId &&
                            ca.locationGroupId === oa.locationGroupId &&
                            ca.productCodeId === oa.productCodeId
                        );
                    })?.length > 0
                );
            });

        await postExceptionListProductCodeLocationGroupAll({
            associationsToSave: associazioniDaInserire,
            associationsToDelete: associazioniDaEliminare,
        });
        navigate("../product-code/location-group-exception-list/list");
    };

    return (
        <Box>
            <SectionHeader title={t("translation:auths.SCOPE_PRC_VIEW_EXCEPTION_LIST_PLAN_CREATE.name")}/>

            <FormProvider {...form}>
                <CardContainer>
                    <Grid container item xs={12} md={12} lg={12} spacing={2} rowSpacing={3}>
                        <DealerAndTenant
                            readOnly={readOnly}
                            md={3}
                            legalEntityName={"legalEntity"}
                            dealerName={"dealer"}
                            dealerIsVisible={true}
                        />
                        {/* COMMERCIAL SEGMENTS */}
                        <Grid item xs={12} md={3}>
                            <FormAutocomplete
                                {...form.register("commercialSegmentId")}
                                label={`${t("pricing:exceptionListProductCodeLocationGroup.form.labels.commercialSegment")}*`}
                                options={commercialSegmentList}
                                isOptionEqualToValue={(option: SelectElementModelFE, value: SelectElementModelFE) => option?.code === value?.code}
                                readOnly={Boolean(formDialog?.readonly) || Boolean(formDialog?.inUse)}
                                handleChange={handleChangeCommercialSegment}
                            />
                        </Grid>

                        {/* COMMERCIAL CATEGORY */}
                        <Grid item xs={12} md={3}>
                            <FormAutocomplete
                                {...form.register("commercialCategoryId")}
                                label={`${t("pricing:exceptionListProductCodeLocationGroup.form.labels.commercialCategory")}*`}
                                options={commercialCategoryList}
                                isOptionEqualToValue={(option: SelectElementModelFE, value: SelectElementModelFE) => option?.code === value?.code}
                                readOnly={!watchAllForm.commercialSegmentId || Boolean(formDialog?.readonly) || Boolean(formDialog?.inUse)}
                                handleChange={handleChangeCommercialCategory}
                            />
                        </Grid>

                        {/* PRODUCT CODE */}
                        <Grid item xs={12} md={3}>
                            <FormAutocomplete
                                {...form.register("exceptionList")}
                                label={`${t("pricing:exceptionListProductCodeLocationGroup.form.labels.exceptionList")}*`}
                                options={exceptionLists}
                                isOptionEqualToValue={(option: ExceptionListSelectFE, value: ExceptionListSelectFE) => option?.code === value?.code}
                                readOnly={!watchAllForm.commercialCategoryId || Boolean(formDialog?.readonly) || Boolean(formDialog?.inUse)}
                                handleChange={handleChangeExceptionList}
                            />
                        </Grid>
                    </Grid>
                </CardContainer>
                <FieldArray
                    {...{
                        control,
                        register,
                        defaultValues: initialValues,
                        getValues,
                        setValue,
                        errors,
                        formDialog,
                        handleChangeProductCode,
                        locationGroupList,
                        handleChangeLocationGroup,
                        label: getValues()?.exceptionList?.label || ""
                    }}
                />

                {!Boolean(formDialog?.readonly) &&
                    <Grid container item xs={12} justifyContent="flex-end" columnGap={2} p={2}>
                        <Grid>
                            <Button variant="outlined"
                                    sx={{marginRight: "15px"}}
                                    color={"accent"}
                                    type={"button"}
                                    disabled={form.formState.isSubmitting}
                                    onClick={() => {
                                        navigate("../product-code/location-group-exception-list/list");
                                    }}
                            >
                                {t("pricing:actions.cancel")}
                            </Button>
                            <Button color={"accent"}
                                    type={"button"}
                                    onClick={form.handleSubmit(handleConfirm)}
                                    disabled={form.formState.isSubmitting}
                            >
                                {t("pricing:actions.confirm")}
                            </Button>
                        </Grid>
                    </Grid>
                }
            </FormProvider>

            <Loading/>
        </Box>
    );
};

export default ExceptionListProductCodeLocationGroupForm;
